import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export type ToastType = 'error' | 'success' | 'info';

export interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toasts: ToastMessage[];
  showToast: (message: string, type?: ToastType) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
    timeoutsRef.current.delete(id);
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    // Create a unique key for this message+type combination
    const toastKey = `${type}:${message}`;

    // Check if a toast with the same message and type already exists
    setToasts(prev => {
      const existingToast = prev.find(t => `${t.type}:${t.message}` === toastKey);

      if (existingToast) {
        // Clear the old timeout
        const oldTimeout = timeoutsRef.current.get(existingToast.id);
        if (oldTimeout) {
          clearTimeout(oldTimeout);
          timeoutsRef.current.delete(existingToast.id);
        }
        // Remove the old toast
        return prev.filter(t => t.id !== existingToast.id);
      }

      return prev;
    });

    // Add the new toast
    const id = Date.now().toString();
    const newToast: ToastMessage = { id, message, type };

    setToasts(prev => [...prev, newToast]);

    // Auto-dismiss after 3 seconds
    const timeout = setTimeout(() => {
      removeToast(id);
    }, 3000);

    timeoutsRef.current.set(id, timeout);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};
